/* Turning a dataset id into something a person can read — and saying why when it cannot.
 *
 * **In `entities/` because three features need it and none may import another.** The dashboard's
 * worklist, the incident queue and the anomaly table all hold an id and a partial catalogue map, and
 * all three previously answered the gap differently: one rendered the raw id, one rendered a single
 * fallback sentence, one rendered nothing. A bare UUID in the cell a steward reads first is the
 * defect this codebase has recorded five times.
 *
 * **The four outcomes are four different facts, and collapsing them loses the useful half.** A
 * failed catalogue read is about the *request* and explains every row at once. A truncated walk is
 * about *us* and explains this row while asserting nothing about the estate. Only a walk that ran to
 * the end makes "this dataset is not in the listing" a claim the screen has grounds for. And a
 * subject that was never a dataset - a contract, say - must not be reported as a missing table,
 * which would be a false statement rather than a missing one.
 */

export type DatasetNaming =
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'gap'; readonly reasonKey: string };

export interface CatalogueNames {
  readonly byId: ReadonlyMap<string, string>;
  /** True when the name walk stopped at its bound with a cursor still outstanding. */
  readonly truncated: boolean;
}

/** Name a dataset id, or report which kind of gap this is. `names` is `null` when the read failed. */
export function nameDataset(id: string, names: CatalogueNames | null): DatasetNaming {
  if (names === null)
    return { kind: 'gap', reasonKey: 'dataset.name.catalogueUnreadable' };
  const name = names.byId.get(id);
  if (name !== undefined) return { kind: 'named', name };
  return {
    kind: 'gap',
    reasonKey: names.truncated
      ? 'dataset.name.catalogueTruncated'
      : 'dataset.name.unlisted',
  };
}
